import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { Alert, Button, InputAdornment, Stack, Typography } from "@mui/material";
import { NodeEdgeMapperContext } from "../../../ContextProviders/NodeEdgeMapperComponentContext";
import { useContext, useMemo, useReducer } from "react";
import { AdjacentType, AppValueTypes, getZombieInstance } from "@iotv/iotv-v3-types";
import { getTypeFilter } from "@iotv/core-services";
import { radioCheckboxColumnDef } from "../../factories/Listerlizer/Listerlizer";
import { getViewDefintionFromMutantZombie } from "../../factories/AttributeDefinitions";
import { viewDefinitionToGridColumnMap } from "../../../v2/components/generic/transformFns";
import { useSelectedObjects } from "../../../hooks/useSelectedObjects";
import { StripedDataGrid } from "../../factories/DataGrid";
import { apiKeyTypeId } from "@iotv/datamodel-core";
import { ActionIcons } from "../../../Iconography";
import { ConsumerDao } from "../../../data/ConsumerDAO/consumerDAO";
import { DialogWrapper } from "../../common/DiaglogWrapper";
import { DebouncedTextField } from "../../common/DebouncedTextField";
import ContentPasteIcon from '@mui/icons-material/ContentPaste';
export const APIKeysView = ({ contextCustomer }) => {
    const { state: { apexNode, nodeEdgeMapper }, dispatch } = useContext(NodeEdgeMapperContext);
    const [state, localDispatch] = useReducer((prev, next) => ({ ...prev, ...next }), { createModalOpen: false });
    if (!apexNode)
        return null;
    const uuidRegex = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;
    const handlePaste = async (event) => {
        event.preventDefault();
        const clipboardData = await navigator.clipboard.readText();
        localDispatch({ newKeyValue: clipboardData.trim() });
    };
    const handleGenerate = async (newKeyValue) => {
        const newKeyId = newKeyValue ?? crypto.randomUUID();
        const newKey = getZombieInstance({
            type: apiKeyTypeId,
            id: newKeyId,
            createdAt: Date.now(),
            pk: contextCustomer.sk
        });
        const { err, data } = await ConsumerDao.save(newKey);
        if (err) {
            console.error('Error creating API Key:', err);
        }
        else if (data) {
            console.log('API Key created successfully:', data);
            dispatch({ type: 'loadItems', value: { items: [data] } });
        }
    };
    const { createModalOpen } = state;
    const handleCreate = async () => {
        localDispatch({ createModalOpen: true });
    };
    const viewDefinitionMap = {
        id: { label: 'Key', editable: false, type: AppValueTypes.STRING, key: 'id', validationRx: uuidRegex },
        createdAt: { label: 'Created', editable: false, type: AppValueTypes.TIMESTAMP_MS, key: 'createdAt' },
    };
    const CreateAPIKeyDialog = () => _jsxs(Stack, { children: [_jsx(Stack, { direction: 'row', justifyContent: 'space-between', alignItems: 'center', children: _jsx(DebouncedTextField, { value: state.newKeyValue,
                    onChange: (e) => {
                        localDispatch({ newKeyValue: e.target.value });
                    },
                    error: !uuidRegex.test(state.newKeyValue ?? ''),
                    variant: 'outlined',
                    label: 'API Key Value',
                    helperText: 'Enter a valid UUID for the API key.',
                    fullWidth: false,
                    InputProps: {
                        endAdornment: _jsx(InputAdornment, { position: "end", onClick: handlePaste, children: _jsx(ContentPasteIcon, {}) })
                    } }) }), _jsx(Button, { disabled: !uuidRegex.test(state.newKeyValue ?? ''), variant: "contained", onClick: () => handleGenerate(state.newKeyValue), endIcon: _jsx(ActionIcons.Create, {}), children: "Add Key" })] });
    return _jsxs(Stack, { children: [createModalOpen && _jsx(DialogWrapper, { open: createModalOpen,
                setClosed: () => localDispatch({ createModalOpen: false }),
                title: 'Create API Key',
                handleClose: () => localDispatch({ createModalOpen: false }),
                header: _jsx(Typography, { variant: "h6", children: "Create API Key" }),
                children: _jsx(CreateAPIKeyDialog, {}) }), _jsx(PseudoAttributeView, { contextObject: contextCustomer, pseudoAttributeType: 'APIKey', viewDefinition: viewDefinitionMap, handleCreate, handleGenerate }), _jsx(CreateAPIKeyDialog, {})] });
};
const PseudoAttributeView = ({ contextObject, pseudoAttributeType, viewDefinition: viewDefinitionIn, handleCreate, handleGenerate }) => {
    const { state: { nodeEdgeMapper }, dispatch } = useContext(NodeEdgeMapperContext);
    const pseudoAttributes = nodeEdgeMapper.getAdjacentEdges(contextObject, { objectTypeId: pseudoAttributeType, adjacencyType: AdjacentType.CHILD }).filter(getTypeFilter(pseudoAttributeType));
    const { state: [[selectedObjects, setSelectedObjects]] } = useSelectedObjects([]);
    const viewDefintion = viewDefinitionIn ?? getViewDefintionFromMutantZombie(pseudoAttributes[0] ?? {});
    const columnMap = viewDefinitionToGridColumnMap(viewDefintion, true);
    const allowSelection = true;
    const columns = useMemo(() => allowSelection
        ? [radioCheckboxColumnDef, ...Object.entries(columnMap).map(([k, v]) => ({ field: k, ...v }))]
        : [...Object.entries(columnMap).map(([k, v]) => ({ field: k, ...v }))], [columnMap, selectedObjects, allowSelection]);
    const handleDelete = async () => {
        await Promise.all(selectedObjects.map(async (item) => {
            try {
                const { err, data } = await ConsumerDao.deleteExact(item);
                if (err) {
                    console.error('Error deleting API Key:', err);
                }
                else if (data) {
                    dispatch({ type: 'removeItems', value: { items: [data] } });
                    console.log('API Key deleted successfully:', data);
                }
            }
            catch (err) {
                console.error('Error deleting API Key:', err);
            }
        }));
        setSelectedObjects([]);
    };
    const updateItemSelection = (rowIds, details) => {
        const selectedIds = 'ids' in rowIds ? Array.from(rowIds.ids) : rowIds;
        setSelectedObjects(selectedIds.map((id) => details.api.getRow(id)).filter((x) => Boolean(x)));
    };
    const dataGridProps = {
        columns,
        rows: pseudoAttributes,
        autoHeight: true,
        getRowClassName: (params) => (params.indexRelativeToCurrentPage % 2 === 0 ? 'even' : 'odd'),
        density: 'compact',
        checkboxSelection: true,
        onRowSelectionModelChange: updateItemSelection,
    };
    return _jsxs(Stack, { children: [_jsx(Typography, { children: "Inbound LNC Integration Keys" }), _jsx(Stack, { direction: 'row', children: _jsxs(Stack, { direction: 'row', justifyContent: 'flex-end', spacing: 1, children: [_jsx(Alert, { severity: 'info', children: "Use these keys in your application to authenticate API requests to LNC." }), _jsxs(Stack, { direction: 'row', justifyContent: 'flex-end', spacing: 1, mb: 1, children: [_jsx(Button, { title: 'Generate Key', onClick: handleCreate, endIcon: _jsx(ActionIcons.Create, {}), children: "Add External Key" }), _jsx(Button, { title: 'Generate Key', onClick: handleGenerate, endIcon: _jsx(ActionIcons.Create, {}), children: "Generate Key" }), _jsx(Button, { title: 'Delete', disabled: selectedObjects.length === 0, onClick: handleDelete, endIcon: _jsx(ActionIcons.Delete, {}), children: "Delete Keys" })] })] }) }), _jsx(StripedDataGrid, { ...dataGridProps, autoHeight: true, hideFooter: true })] });
};
